import type { GrammarExercise } from '../exercise-types';

// Practice exercises for the Connecting Ideas section. Difficulty:
// upper-intermediate/advanced — discourse-level choices (which connector fits
// the logical relationship, register, feedback and opinion phrases). Contexts
// follow E2E's critical, feminist, empowering methodology.
export const connectingExercises: Record<string, GrammarExercise[]> = {
  'uses-of-rather': [
    {
      kind: 'multiple-choice',
      prompt: 'Choose the more formal way to say “I’m quite busy this week”: I’m ___ busy this week — I’m preparing my conference talk.',
      options: ['rather', 'would rather', 'rather than'],
      correctIndex: 0,
      explanation: 'Before an adjective, rather indicates a degree — similar to quite, but more formal.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Helena wanted to be a software engineer ___ a secretary, so she went back to school.',
      options: ['rather than', 'would rather', 'rather'],
      correctIndex: 0,
      explanation: 'Rather than compares two alternatives and gives more importance to one — like instead of.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I’d ___ lead the project myself than wait for someone to invite me.',
      answers: ['rather'],
      explanation: 'Would rather (’d rather) + verb expresses a preference between two options.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'I’d rather to stay and defend my idea than leave the meeting.',
        'I’d rather stay and defend my idea than leave the meeting.',
        'I’d rather staying and defend my idea than leave the meeting.',
      ],
      correctIndex: 1,
      explanation: 'Would rather is followed by the verb in the infinitive without to.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Can we schedule the mentoring session for Thursday ___ than Friday?',
      answers: ['rather'],
      explanation: 'Rather than indicates an alternative — one option is preferred over the other.',
    },
    {
      kind: 'matching',
      instruction: 'Match each use of rather with its meaning:',
      pairs: [
        { left: 'rather + adjective', right: 'degree — a formal quite' },
        { left: 'rather than', right: 'alternatives — like instead of' },
        { left: 'would rather', right: 'preference between two options' },
      ],
    },
  ],

  'giving-feedback': [
    {
      kind: 'multiple-choice',
      prompt: 'Which comment is SPECIFIC feedback on a colleague’s presentation?',
      options: [
        '“Your presentation was great.”',
        '“Starting your presentation off with a provocative question got my attention.”',
        '“Good job!”',
      ],
      correctIndex: 1,
      explanation: 'Specific feedback avoids ambiguous language and says exactly what worked.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“Next time, try a summary slide with one or two take-home points for the audience.” In the SMART framework, this feedback is ___.',
      options: ['measurable', 'judgmental', 'vague'],
      correctIndex: 0,
      explanation: 'Measurable feedback suggests quantifiable objectives, so there is no uncertainty about the goal.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Feedback given directly after the presentation is more effective than feedback given weeks later — in SMART terms, it is ___.',
      answers: ['timely'],
      explanation: 'The T in SMART stands for timely: give feedback right after the performance.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which of these comments is EFFECTIVE feedback?',
      options: [
        '“Eye contact.”',
        '“Don’t be nervous.”',
        '“You looked at your notes more than you looked at the audience during the first thirty seconds of your speech.”',
      ],
      correctIndex: 2,
      explanation: 'Effective feedback is specific and descriptive, not a vague label or an empty instruction.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Helpful feedback is constructive, descriptive, and non-___.',
      answers: ['judgmental', 'judgemental'],
      explanation: 'Good feedback describes what happened instead of judging the person.',
    },
    {
      kind: 'matching',
      instruction: 'Match each letter of SMART feedback with its meaning:',
      pairs: [
        { left: 'Specific', right: 'avoid ambiguous language' },
        { left: 'Measurable', right: 'suggest quantifiable objectives' },
        { left: 'Achievable', right: 'the goal should be attainable' },
        { left: 'Timely', right: 'give it right after the performance' },
      ],
    },
  ],

  'expressing-opinions': [
    {
      kind: 'multiple-choice',
      prompt: '“Every community deserves a public library.” — “I couldn’t agree ___!”',
      options: ['more', 'less', 'most'],
      correctIndex: 0,
      explanation: 'I couldn’t agree more expresses total agreement — it’s impossible to agree any more than this.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In the debate about equal pay, which phrase expresses DISAGREEMENT?',
      options: ['I beg to differ.', 'You have a point there.', 'We are of one mind on this.'],
      correctIndex: 0,
      explanation: 'I beg to differ is a polite, confident way to disagree.',
    },
    {
      kind: 'fill-blank',
      prompt: 'As far as I’m ___, every girl deserves access to quality education.',
      answers: ['concerned'],
      explanation: 'As far as I’m concerned introduces your personal opinion.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“I agree with you up to a ___, but online classes can’t fully replace community spaces.”',
      options: ['point', 'dot', 'line'],
      correctIndex: 0,
      explanation: 'I agree with you up to a point expresses partial agreement before introducing a but.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I’m sorry, but I believe your argument doesn’t hold ___.',
      answers: ['water'],
      explanation: 'An argument that doesn’t hold water is not logical or convincing.',
    },
    {
      kind: 'matching',
      instruction: 'Match each phrase with its function in a discussion:',
      pairs: [
        { left: 'I take a different view.', right: 'disagreeing' },
        { left: 'You’ve made a good point.', right: 'agreeing' },
        { left: 'I see what you mean, but…', right: 'agreeing only partially' },
        { left: 'It seems to me that…', right: 'introducing your opinion' },
      ],
    },
  ],

  'english-global-language': [
    {
      kind: 'matching',
      instruction: 'Match each American English word with its British English equivalent:',
      pairs: [
        { left: 'Vacation', right: 'Holiday' },
        { left: 'Elevator', right: 'Lift' },
        { left: 'Movie', right: 'Film' },
        { left: 'Pants', right: 'Trousers' },
        { left: 'Sweater', right: 'Jumper' },
      ],
    },
    {
      kind: 'multiple-choice',
      prompt: 'At a conference in London, Bianca asks where to leave her rental car. In British English, she should look for the ___.',
      options: ['car park', 'parking lot', 'garage sale'],
      correctIndex: 0,
      explanation: 'Parking lot is American English; the British word is car park.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which spelling is BRITISH English?',
      options: ['colour', 'color', 'kolor'],
      correctIndex: 0,
      explanation: 'British English keeps -our in words like colour and favourite; American English uses -or.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Today, more people speak English as an additional language than as a ___ language.',
      answers: ['first', 'native'],
      explanation: 'English is a global language — most of its speakers learned it as an additional language.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'According to linguists, what matters most when you speak English with people around the world?',
      options: ['Intelligibility — being understood', 'A perfect native speaker accent', 'Sounding exactly British'],
      correctIndex: 0,
      explanation: 'The goal is intelligibility: your accent shows your history, and English belongs to everyone who speaks it.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Larissa, a Brazilian speaker, says “I have 30 years.” The standard English version is:',
      options: ['I am 30 years old.', 'I have 30 years old.', 'I do 30 years.'],
      correctIndex: 0,
      explanation: '“I have 30 years” comes from Portuguese influence — English uses the verb be for age.',
    },
  ],

  'sentence-connectors': [
    {
      kind: 'multiple-choice',
      prompt: 'Luiza worked in sales for five years, and she worked another three in accounting. ___, she is eligible for a management position.',
      options: ['Therefore', 'However', 'Although'],
      correctIndex: 0,
      explanation: 'Therefore introduces the result of what came before.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which connector ADDS something to a previous idea?',
      options: ['Moreover', 'Yet', 'Provided that'],
      correctIndex: 0,
      explanation: 'Moreover, in addition, and further all add something in initial position.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She didn’t just meet her targets. ___ a matter of fact, she doubled them.',
      answers: ['as'],
      explanation: 'As a matter of fact — like in fact and indeed — makes what you say stronger.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'You can lead this project ___ you keep the whole team informed.',
      options: ['as long as', 'as a result', 'in addition'],
      correctIndex: 0,
      explanation: 'As long as expresses a condition and starts a subordinate clause.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which connector introduces an unexpected contrast (concession) and starts a subordinate clause?',
      options: ['Even though', 'In contrast', 'Hence'],
      correctIndex: 0,
      explanation: 'Although, even though, and despite the fact that introduce concessions in a subordinate clause.',
    },
    {
      kind: 'matching',
      instruction: 'Match each connector with the relationship it expresses:',
      pairs: [
        { left: 'Moreover', right: 'adding something' },
        { left: 'However', right: 'contrast' },
        { left: 'Therefore', right: 'result' },
        { left: 'Because', right: 'cause' },
        { left: 'Provided that', right: 'condition' },
      ],
      explanation: 'Discourse connectors show your readers how each idea relates to the others.',
    },
  ],

  'connectors-of-contrast': [
    {
      kind: 'multiple-choice',
      prompt: '___ all the challenges, Rosa finished her degree while raising two kids.',
      options: ['Despite', 'Although', 'However'],
      correctIndex: 0,
      explanation: 'Despite and in spite of are used before a noun or a gerund, not a full clause.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'Despite she was nervous, Camila spoke up in the board meeting.',
        'Although she was nervous, Camila spoke up in the board meeting.',
        'However she was nervous, Camila spoke up in the board meeting.',
      ],
      correctIndex: 1,
      explanation: 'Although and even though come before a complete clause (subject + verb).',
    },
    {
      kind: 'fill-blank',
      prompt: 'Even ___ it was her first salary negotiation, Marta asked for what she deserved.',
      answers: ['though'],
      explanation: 'Even though = although: it introduces a contrast before a subject + verb.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I love my work in spite ___ the long hours.',
      answers: ['of'],
      explanation: 'In spite of — like despite — is followed by a noun or a gerund.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I prefer speaking up in meetings, ___ my colleague prefers writing her ideas down.',
      options: ['whereas', 'despite', 'due to'],
      correctIndex: 0,
      explanation: 'While, whereas, and the more formal whilst contrast two facts in the same sentence.',
    },
    {
      kind: 'matching',
      instruction: 'Match each contrast connector with what comes after it:',
      pairs: [
        { left: 'Despite', right: 'a noun or a gerund (-ing)' },
        { left: 'Even though', right: 'a clause (subject + verb)' },
        { left: 'However', right: 'a new sentence, followed by a comma' },
      ],
    },
  ],

  'connectors-cause-effect': [
    {
      kind: 'multiple-choice',
      prompt: 'Vera founded the women’s collective ___ she wanted others to have the support she never had.',
      options: ['because', 'therefore', 'as a result'],
      correctIndex: 0,
      explanation: 'Because, since, and as introduce the cause; therefore and as a result introduce the effect.',
    },
    {
      kind: 'multiple-choice',
      prompt: '___ the heavy traffic, Renata joined the strategy meeting online.',
      options: ['Due to', 'Because', 'So'],
      correctIndex: 0,
      explanation: 'Due to and because of come before a noun; because comes before a full clause.',
    },
    {
      kind: 'fill-blank',
      prompt: 'It was raining, ___ I stayed home and practiced my English presentation.',
      answers: ['so'],
      explanation: 'So introduces the effect or consequence after a comma.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The bridge was unstable; ___, engineers closed it.',
      options: ['consequently', 'because', 'since'],
      correctIndex: 0,
      explanation: 'Connectors of effect like consequently appear after punctuation — a comma, semicolon, or period.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The price of oil increased; as a ___, bus fares went up.',
      answers: ['result', 'consequence'],
      explanation: 'As a result introduces the effect of the cause stated before it.',
    },
    {
      kind: 'matching',
      instruction: 'Match each connector with how it works in a sentence:',
      pairs: [
        { left: 'Because', right: 'introduces the cause, before a clause' },
        { left: 'Due to', right: 'introduces the cause, before a noun' },
        { left: 'Therefore', right: 'introduces the effect, after punctuation' },
      ],
    },
  ],

  conjunctions: [
    {
      kind: 'multiple-choice',
      prompt: 'Jéssica isn’t going to the conference, ___ will Tamara.',
      options: ['nor', 'or', 'for'],
      correctIndex: 0,
      explanation: 'Nor — the N in FANBOYS — joins two negative ideas.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Lia is clearly exhausted, ___ she insists on finishing the proposal tonight.',
      options: ['yet', 'so', 'for'],
      correctIndex: 0,
      explanation: 'Yet indicates a contrast between two independent clauses.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Not ___ did she finish her degree, but she also started her own business.',
      answers: ['only'],
      explanation: 'Not only / but also is a correlative conjunction — a pair that works together.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I have finished ___ my English essay nor my history essay.',
      answers: ['neither'],
      explanation: 'Neither pairs with nor to join two negative alternatives.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence has correct parallel structure?',
      options: [
        'I work quickly and careful.',
        'I work quickly and carefully.',
        'I work quick and carefully.',
      ],
      correctIndex: 1,
      explanation: 'Phrases joined by conjunctions must be parallel: two adverbs, not an adverb and an adjective.',
    },
    {
      kind: 'matching',
      instruction: 'Match each FANBOYS conjunction with what it indicates:',
      pairs: [
        { left: 'for', right: 'a reason or cause' },
        { left: 'but', right: 'a contrast' },
        { left: 'or', right: 'an alternative' },
        { left: 'so', right: 'a result' },
      ],
      explanation: 'Coordinating conjunctions join words, phrases, and clauses of equal grammatical rank.',
    },
  ],
};
